// @givmo/board — About the board: the orientation materials, assembled from the board's own records
// (CBG-YR4-ABOUT, g7).
//
// One read assembles what the board has recorded, and nothing it hasn't: who serves and in what
// capacity (no addresses), the duties the board adopted for its officers (never Givmo's starting
// point), its recorded meeting rules with their citations, its next meeting, its governing documents
// in force, and its minutes and written consents of the last twelve months. Standard records only:
// a closed session's minutes are never assembled here, whoever reads (a director's own right to open
// them stays on the record's page, logged, D-CBG-25). Nothing is stored, and no AI writes or
// summarizes any of it.

import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Skeleton } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { useDirectorReadsMayFire } from '../../director/read-gate';
import { displayDate, displayLongDate } from '../../lib/display';
import { DirectorBoardContent } from './director-board-content';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { DirectorShell } from './director-shell';
import { isCommitteeOnly } from '../../director/director-wire';
import { documentKindLabel } from '../govdocs/govdocs-decode';
import { displayMeetingStart } from '../meetings/meetings-decode';
import { parameterLabel } from '../parameters/parameters-decode';
import { capacityLineOf } from '../roster/officer-roles';
import { dutyWords } from '../roster/officer-duties';
import { extractAbout } from '../onboarding/onboarding-decode';
import { onboardingWords as w } from '../onboarding/onboarding-words';

const H2 = 'font-sans text-s16 font-w700 tracking-givmo text-text-strong';
const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const SMALL = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const RULE_LABEL =
  'w-[260px] shrink-0 font-sans text-s14 font-w400 tracking-givmo text-text-secondary';
const RECORD_LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

export function DirectorAboutPage() {
  const { board } = useDirectorBoardAddress();
  // CBG-YR6-OUTSIDE (review-215's F5): About the board is the board's alone; a seat for someone who isn't
  // on the board, here by address, meets the one not-found and nothing of the board's.
  if (isCommitteeOnly(board)) return <DirectorBoardOnlyPage testId="page-director-about" />;
  return <DirectorAboutPageForTheBoard />;
}

function DirectorAboutPageForTheBoard() {
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const aboutQuery = useQuery({
    queryKey: ['board-director-about', orgRef ?? ''],
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardAbout({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractAbout(data);
    },
  });
  const about = aboutQuery.data ?? null;
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);

  return (
    <DirectorShell
      title={w.aboutTitle}
      description={w.aboutLine}
      identity={directorBoardIdentity(board)}
      testId="page-director-about"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {about === null && aboutQuery.isPending && mayFire ? (
          <Skeleton className="h-84 w-full rounded-lg" />
        ) : null}
        {about !== null ? (
          <>
            <Card testId="about-people">
              <CardBody className="flex flex-col gap-12">
                <h2 className={H2}>{w.whoServes}</h2>
                <ul className="grid grid-cols-1 gap-x-24 gap-y-8 sm:grid-cols-2">
                  {(about.people ?? []).map((person) => (
                    <li key={person.board_member_id} className="flex flex-col gap-0">
                      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {person.display_name}
                      </span>
                      <span className={SMALL}>
                        {capacityLineOf(person.is_director, person.title_labels ?? [])}
                        {person.is_you ? ` · ${w.you}` : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>

            {about.duties && (about.duties.titles ?? []).length > 0 ? (
              <Card testId="about-duties">
                <CardBody className="flex flex-col gap-12">
                  <h2 className={H2}>{w.whoDoesWhat}</h2>
                  <dl className="flex flex-col gap-10">
                    {(about.duties.titles ?? []).map((title) => (
                      <div
                        key={title.title_id ?? title.label}
                        className="flex flex-col gap-2 sm:flex-row sm:gap-16"
                      >
                        <dt className="w-[120px] shrink-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                          {title.label}
                        </dt>
                        <dd className={LINE}>{(title.acts ?? []).map(dutyWords).join(' · ')}</dd>
                      </div>
                    ))}
                  </dl>
                  {about.duties.adopted_at ? (
                    <p className={SMALL}>
                      {w.dutiesAdopted(displayLongDate(about.duties.adopted_at) ?? '')}
                    </p>
                  ) : null}
                </CardBody>
              </Card>
            ) : null}

            {(about.rules ?? []).length > 0 || about.next_meeting ? (
              <Card testId="about-rules">
                <CardBody className="flex flex-col gap-12">
                  <h2 className={H2}>{w.howMeetingsWork}</h2>
                  <dl className="flex flex-col gap-8">
                    {(about.rules ?? []).map((rule) => (
                      <div
                        key={`${rule.parameter_kind}-${rule.meeting_type ?? ''}`}
                        className="flex flex-col gap-0 sm:flex-row sm:gap-16"
                      >
                        <dt className={RULE_LABEL}>{parameterLabel(rule)}</dt>
                        <dd className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                          {rule.value_summary}
                          {rule.bylaw_citation ? (
                            <span className="font-w400 text-text-tertiary">
                              {' '}
                              · {rule.bylaw_citation}
                            </span>
                          ) : null}
                        </dd>
                      </div>
                    ))}
                    {about.next_meeting ? (
                      <div className="flex flex-col gap-0 sm:flex-row sm:gap-16">
                        <dt className={RULE_LABEL}>{w.nextMeeting}</dt>
                        <dd className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                          {about.next_meeting.title}{' '}
                          <span className="font-w400 text-text-tertiary">
                            · {displayMeetingStart(about.next_meeting)}
                          </span>
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                </CardBody>
              </Card>
            ) : null}

            <Card testId="about-documents">
              <CardBody className="flex flex-col gap-12">
                <h2 className={H2}>{w.documents}</h2>
                {(about.documents ?? []).length === 0 ? (
                  <p className={LINE}>{w.nothingRecorded}</p>
                ) : (
                  <ul className="flex flex-col gap-8">
                    {(about.documents ?? []).map((doc) => (
                      <li
                        key={doc.record_id}
                        className="flex flex-wrap items-baseline justify-between gap-x-16 gap-y-2"
                      >
                        <Link
                          to={BOARD_DIRECTOR_PAGES.record}
                          params={{ recordId: doc.record_id }}
                          search={search}
                          className={RECORD_LINK}
                        >
                          {doc.title || documentKindLabel(doc.governing_document_kind)}
                        </Link>
                        <span className={SMALL}>{w.adoptedOn(displayDate(doc.occurred_on))}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card testId="about-decisions">
              <CardBody className="flex flex-col gap-12">
                <div className="flex flex-col gap-4">
                  <h2 className={H2}>{w.recentDecisions}</h2>
                  <p className={SMALL}>{w.recentLine}</p>
                </div>
                {(about.decisions ?? []).length === 0 ? (
                  <p className={LINE}>{w.nothingRecorded}</p>
                ) : (
                  <ul className="flex flex-col gap-8">
                    {(about.decisions ?? []).map((decision) => (
                      <li
                        key={decision.record_id}
                        className="flex flex-wrap items-baseline justify-between gap-x-16 gap-y-2"
                      >
                        <Link
                          to={BOARD_DIRECTOR_PAGES.record}
                          params={{ recordId: decision.record_id }}
                          search={search}
                          className={RECORD_LINK}
                        >
                          {decision.title}
                        </Link>
                        <span className={SMALL}>
                          {w.decidedOn(decision.record_kind, displayDate(decision.approved_at))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </>
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}
